/**
 * CRM Service - Migrated to event-sourced CRM library.
 *
 * This file now re-exports from the new CRM library for backward compatibility.
 * Event-sourced CRM operations (contacts, companies, deals) now use React Query
 * hooks for better caching, optimistic updates, and error handling.
 *
 * Task management (labels, pipelines, tasks) still uses the old CRM app REST API.
 *
 * MIGRATION NOTE:
 * - contactService, companyService, dealService → Use hooks from @/lib/crm
 * - taskService, labelService, taskPipelineService → Still use REST API (below)
 */

import { apiCall, buildQueryString } from '@/lib/http/client';
import { logger } from '@/lib/logger';

// ===========================
// Event-Sourced CRM (contacts, companies, deals)
// ===========================

// Re-export from new CRM library for backward compatibility
export { createCRMObjectClient } from './core/CRMObjectClient';
export { createEventBuilder } from './core/EventBuilder';

// For components that still use the old service pattern, redirect to library
import type {
  AssociationDetail,
  CompanyCreateData,
  CompanyUpdateData,
  ContactCreateData,
  ContactUpdateData,
  CrmObject,
  DealCreateData,
  DealUpdateData,
  Label,
  LabelCreateUpdate,
  LabelFilters,
  LabelsListResponse,
} from '../../types/crm';
import { getPropertyValue } from '../../types/crm';
import { createCRMObjectClient } from './core/CRMObjectClient';
import {
  appendDraftEvents,
  assertValidDraftEntities,
  type DraftEntity,
} from './core/draftEntities';
import {
  reconcileOptimisticCreate,
  reconcileOptimisticDrafts,
} from './core/reconcileOptimisticCreate';
import { submitEventsOptimistic } from './store/submitEventsOptimistic';

type CRMFilters = Record<
  string,
  string | number | boolean | string[] | undefined
>;

// ===========================
// Contact upsert errors
// ===========================

/**
 * Thrown when a contact with the given email already exists AND has a lifecycle stage.
 * The UI should display this contact to the user rather than silently merging.
 */
export class ContactAlreadyExistsError extends Error {
  public readonly existingContact: CrmObject;

  constructor(existingContact: CrmObject) {
    super('Contact already exists with a lifecycle stage');
    this.name = 'ContactAlreadyExistsError';
    this.existingContact = existingContact;
  }
}

// ===========================
// Helpers for contact upsert
// ===========================

/** Extract the first email string from a multi-value email property. */
function extractFirstEmail(value: unknown): string | null {
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === 'string' && first.trim()) {
      return first.trim();
    }
    return null;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
}

/** Check if a form value is effectively empty. */
function isEmptyValue(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
}

/** Deep equality check for property values (handles objects like phone numbers). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return a === b;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a !== 'object') {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

// ===========================
// Contact email dedup prefetch cache
// ===========================

/**
 * Short-lived cache of "does a contact with this email already exist?", keyed by
 * `${orgId}:${email}`. Populated by contactService.prefetchExistingByEmail when the
 * user enters the email in the create form, then consumed by contactService.create
 * to skip the pre-submit lookup round-trip on the common "no existing match" path.
 * The backend dedup (unique constraints / merge cascade) remains the final guard —
 * the same lookup-then-create race the prior synchronous lookup already had.
 */
interface ContactEmailDedupEntry {
  hasMatch: boolean;
  checkedAt: number;
}

const CONTACT_EMAIL_DEDUP_TTL_MS = 30_000;
const contactEmailDedupCache = new Map<string, ContactEmailDedupEntry>();

function contactEmailDedupKey(orgId: number, email: string): string {
  return `${orgId}:${email}`;
}

/**
 * Look up an existing contact by exact email, requesting the given property keys
 * (plus display fields) so the caller can diff them. Returns null when there is no
 * match or when the lookup itself fails (caller falls through to a normal create).
 */
async function lookupExistingContactByEmail(
  client: ReturnType<typeof createCRMObjectClient>,
  email: string,
  propertyKeys: string[]
): Promise<CrmObject | null> {
  try {
    const existing = await client.list(
      {
        filter_groups: [
          {
            filters: [
              { property: 'ahoy_email', operator: 'exact', value: email },
            ],
          },
        ],
        properties: [
          ...new Set([
            ...propertyKeys,
            // EntityBadge needs these for display name and avatar
            'ahoy_display_name',
            'ahoy_first_name',
            'ahoy_last_name',
            'ahoy_profile_photo_url',
          ]),
        ],
        limit: 1,
      },
      { include_associated_objects: true }
    );
    return existing.results.length > 0 ? existing.results[0] : null;
  } catch {
    // Lookup failed — signal "no match" so the caller falls through to create.
    return null;
  }
}

/** Test-only: reset the email-dedup prefetch cache between tests. */
export function __clearContactEmailDedupCacheForTesting(): void {
  contactEmailDedupCache.clear();
}

// Simplified service objects that delegate to the library
export const contactService = {
  list: (orgId: number, filters?: CRMFilters) => {
    const client = createCRMObjectClient(orgId, 'ahoy_contact');
    return client.list(filters);
  },
  get: (orgId: number, entityId: string) => {
    const client = createCRMObjectClient(orgId, 'ahoy_contact');
    return client.get(entityId);
  },
  /**
   * Warm the email-dedup cache ahead of a create. Call this (debounced) when the
   * user enters/changes the email in the create form so contactService.create can
   * skip its pre-submit existence lookup on the common "new contact" path. Best
   * effort: on failure the cache entry is dropped so create() does a live lookup.
   */
  prefetchExistingByEmail: async (
    orgId: number,
    emailValue: unknown
  ): Promise<void> => {
    const email = extractFirstEmail(emailValue);
    if (!email) {
      return;
    }
    const key = contactEmailDedupKey(orgId, email);
    const client = createCRMObjectClient(orgId, 'ahoy_contact');
    try {
      const existing = await client.list({
        filter_groups: [
          {
            filters: [
              { property: 'ahoy_email', operator: 'exact', value: email },
            ],
          },
        ],
        properties: ['ahoy_email'],
        limit: 1,
      });
      contactEmailDedupCache.set(key, {
        hasMatch: existing.results.length > 0,
        checkedAt: Date.now(),
      });
    } catch {
      // Best-effort — drop any stale entry so create() does a live lookup.
      contactEmailDedupCache.delete(key);
    }
  },
  create: async (orgId: number, data: ContactCreateData, userId: number) => {
    const client = createCRMObjectClient(orgId, 'ahoy_contact');

    // Extract associations array, legacy company_entity_id field,
    // overwrite_targets, and draft_entities
    const {
      associations,
      company_entity_id,
      overwrite_targets,
      draft_entities,
      ...properties
    } = data as ContactCreateData & {
      associations?: Array<{
        association_type_name: string;
        target_entity_id: string;
      }>;
      company_entity_id?: string;
      overwrite_targets?: Array<{
        entity_id: string;
        association_type_name: string;
      }>;
      draft_entities?: DraftEntity[];
    };

    // Snapshot once (deep clone) — submitEventsOptimistic's 409-retry rebuild
    // replays the build callback, so it must see the same drafts every time; a
    // deep clone (not a shallow array copy) guarantees a caller mutation of a
    // draft's properties/associations between attempts can't change the replay.
    const drafts: DraftEntity[] = structuredClone(draft_entities ?? []);

    // Build associations array - support both new format and legacy field
    let finalAssociations = associations || [];
    if (company_entity_id && !associations) {
      // Legacy format: convert company_entity_id to associations array
      finalAssociations = [
        {
          association_type_name: 'ahoy_contact_to_company',
          target_entity_id: company_entity_id,
        },
      ];
    }

    // The draft batch is validated per-branch below (create vs. matched-contact
    // update), because the two branches emit different parent events: the create
    // path emits an objectCreated + one associationAdded per association, while a
    // matched-contact update emits neither the create nor the associations the
    // contact already has. Validating with the create-path count up front would
    // over-count on the update branch and could falsely reject a valid batch near
    // the backend per-batch cap. Each branch validates before its optimistic apply.

    // --- Upsert: check if a contact with this email already exists ---
    // The form passes ahoy_* property names directly (ContactCreateData type is legacy)
    const props = properties as Record<string, unknown>;
    const emailValue = extractFirstEmail(props.ahoy_email);

    if (emailValue) {
      // Fast path: if a recent prefetch (fired when the user entered the email in
      // the create form) found no contact with this email, skip the pre-submit
      // lookup round-trip and create directly. Consume the entry so a later create
      // in the same session can't reuse it stale; the TTL bounds staleness and the
      // backend dedup remains the final guard — the same lookup-then-create race
      // the prior synchronous lookup already had.
      const dedupKey = contactEmailDedupKey(orgId, emailValue);
      const cachedDedup = contactEmailDedupCache.get(dedupKey);
      contactEmailDedupCache.delete(dedupKey);
      const skipLookup =
        cachedDedup !== undefined &&
        Date.now() - cachedDedup.checkedAt < CONTACT_EMAIL_DEDUP_TTL_MS &&
        !cachedDedup.hasMatch;

      // Step 1: Look up existing contact by email (unless a fresh prefetch already
      // ruled out a match). A failed lookup returns null → normal create.
      const existingContact = skipLookup
        ? null
        : await lookupExistingContactByEmail(
            client,
            emailValue,
            Object.keys(props)
          );

      // Step 2: If we found a match, update it (errors here must propagate
      // to avoid creating a duplicate contact).
      if (existingContact) {
        // Guard: if the existing contact has a lifecycle stage, don't merge
        const hasLifecycleStage = existingContact.associations?.some(
          a => a.association_type_name === 'ahoy_contact_to_lifecycle_stage'
        );
        if (hasLifecycleStage) {
          throw new ContactAlreadyExistsError(existingContact);
        }

        // Diff: find properties that differ between form and existing contact
        const changedProperties: Record<string, unknown> = {};
        for (const [key, formValue] of Object.entries(props)) {
          if (isEmptyValue(formValue)) {
            continue;
          }
          const existingValue = getPropertyValue(
            existingContact.properties?.[key]
          );
          if (!deepEqual(formValue, existingValue)) {
            changedProperties[key] = formValue;
          }
        }

        // Filter out associations the contact already has
        const newAssociations = finalAssociations.filter(
          assoc =>
            !existingContact.associations?.some(
              a =>
                a.association_type_name === assoc.association_type_name &&
                a.target_entity_id === assoc.target_entity_id
            )
        );

        const hasPropertyChanges = Object.keys(changedProperties).length > 0;
        const hasAssociations = newAssociations.length > 0;

        // Nothing to do — contact already matches
        if (!hasPropertyChanges && !hasAssociations && drafts.length === 0) {
          return { entityId: existingContact.entity_id };
        }

        // Validate the folded batch with THIS branch's actual parent event
        // count before any optimistic apply: a matched-contact update emits only
        // the changed-properties event (when there are changes) plus one
        // associationAdded per NOT-already-present association — never an
        // objectCreated, and never the associations the contact already has.
        assertValidDraftEntities(
          drafts,
          (hasPropertyChanges ? 1 : 0) + newAssociations.length
        );

        // Deliberately still AWAITED, unlike the create path below. A failed
        // update on a matched contact must reject so the caller can surface it
        // and the user does not end up creating a duplicate (see the "Step 2"
        // note above and the `propagate errors from update path` test). The
        // modal therefore stays open for this branch and renders the inline
        // error, which is why `errorToast` remains suppressed here.
        try {
          await submitEventsOptimistic({
            orgId,
            userId,
            entityTypeName: 'ahoy_contact',
            primaryEntityId: existingContact.entity_id,
            fallbackSeq: existingContact.last_event_seq,
            overwriteTargets: overwrite_targets,
            errorToast: false,
            build: b => {
              if (hasPropertyChanges) {
                b.propertiesUpdated(
                  existingContact.entity_id,
                  'ahoy_contact',
                  changedProperties
                );
              }
              newAssociations.forEach(a =>
                b.associationAdded(
                  existingContact.entity_id,
                  'ahoy_contact',
                  a.association_type_name,
                  a.target_entity_id
                )
              );
              appendDraftEvents(b, drafts);
            },
          });
        } catch (err: unknown) {
          // Helper already rolled back and suppressed the toast
          // (errorToast:false → caller renders inline). This awaited branch
          // also folds drafts, so a draft-submit failure would be silent
          // otherwise — log for telemetry (draftEntityIds included), then
          // re-propagate so the caller can surface it inline.
          logger.error(
            'Optimistic CRM contact update with drafts failed; batch rolled back',
            err instanceof Error ? err : new Error(String(err)),
            {
              component: 'crm_events',
              entityType: 'ahoy_contact',
              orgId,
              entityId: existingContact.entity_id,
              draftEntityIds: drafts.map(d => d.entity_id),
            }
          );
          throw err;
        }

        void reconcileOptimisticDrafts(orgId, drafts);
        return { entityId: existingContact.entity_id };
      }
    }

    // --- No existing match: normal create path ---
    // Coordinates with useCreateCrmObject's instant-creation path — both mint
    // their own client-side entity id independently; whichever submit lands
    // second reconciles the store entry.
    const entityId = crypto.randomUUID();

    // Validate the folded batch with the create path's actual parent event
    // count (its objectCreated + one associationAdded per association) before
    // the optimistic apply inside submitEventsOptimistic.
    assertValidDraftEntities(drafts, 1 + finalAssociations.length);

    // `submitEventsOptimistic` is async, but `applyOptimisticBatch` runs
    // BEFORE its first `await` — so the store already holds the new entity by
    // the time this call returns its promise. Deliberately NOT awaited when
    // there are no drafts: the caller (the create modal) resolves on the
    // optimistic apply and closes immediately, while the network round-trip,
    // reconciliation and rollback continue detached. Mirrors
    // `core/createObject.ts`.
    //
    // The error toast is REQUIRED here, not optional: once the modal closes it
    // is no longer around to render the inline error, so suppressing the toast
    // would make a failed create silent. The helper rolls the optimistic entry
    // back and toasts on terminal failure.
    //
    // When drafts are present, the caller renders the drafts inline and needs
    // to know the submit outcome, so this IS awaited — with the toast
    // suppressed (the caller shows the error inline instead).
    const submit = submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_contact',
      primaryEntityId: entityId,
      overwriteTargets: overwrite_targets,
      ...(drafts.length > 0 ? { errorToast: false as const } : {}),
      build: b => {
        b.objectCreated(entityId, 'ahoy_contact', properties);
        finalAssociations.forEach(a =>
          b.associationAdded(
            entityId,
            'ahoy_contact',
            a.association_type_name,
            a.target_entity_id
          )
        );
        appendDraftEvents(b, drafts);
      },
    });

    if (drafts.length > 0) {
      try {
        const response = await submit;
        void reconcileOptimisticCreate(
          orgId,
          'ahoy_contact',
          entityId,
          response
        );
        void reconcileOptimisticDrafts(orgId, drafts);
        return { entityId };
      } catch (err: unknown) {
        // Helper already rolled back (parent + drafts) and suppressed the
        // toast (errorToast:false → caller renders inline). Log for
        // telemetry (this is the one path with no toast), then re-propagate.
        logger.error(
          'Optimistic CRM create with drafts failed; batch rolled back',
          err instanceof Error ? err : new Error(String(err)),
          {
            component: 'crm_events',
            entityType: 'ahoy_contact',
            orgId,
            entityId,
            draftEntityIds: drafts.map(d => d.entity_id),
          }
        );
        throw err;
      }
    }

    void submit
      .then(response => {
        void reconcileOptimisticCreate(
          orgId,
          'ahoy_contact',
          entityId,
          response
        );
      })
      .catch(() => {
        // Rollback + toast already handled inside submitEventsOptimistic.
      });

    return { entityId };
  },
  update: (
    orgId: number,
    contact: CrmObject,
    data: ContactUpdateData,
    userId: number
  ) => {
    // Collect all property updates to bundle them into a single event
    const properties: Record<string, unknown> = {};

    if (data.first_name !== undefined) {
      properties.ahoy_first_name = data.first_name;
    }
    if (data.last_name !== undefined) {
      properties.ahoy_last_name = data.last_name;
    }
    if (data.email !== undefined) {
      properties.ahoy_email = data.email ? [data.email] : undefined;
    }
    // Phone is already PhoneNumber | null from PropertyField
    if (data.phone !== undefined) {
      properties.ahoy_phone_number = data.phone;
    }

    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_contact',
      primaryEntityId: contact.entity_id,
      fallbackSeq: contact.last_event_seq,
      errorToast: false,
      build: b =>
        b.propertiesUpdated(contact.entity_id, 'ahoy_contact', properties),
    });
  },
  addLifecycleStage: (
    orgId: number,
    contact: CrmObject,
    stageEntityId: string,
    userId: number
  ) => {
    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_contact',
      primaryEntityId: contact.entity_id,
      fallbackSeq: contact.last_event_seq,
      build: b =>
        b.associationAdded(
          contact.entity_id,
          'ahoy_contact',
          'ahoy_contact_to_lifecycle_stage',
          stageEntityId
        ),
    });
  },
  delete: (orgId: number, contact: CrmObject, userId: number) => {
    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_contact',
      primaryEntityId: contact.entity_id,
      fallbackSeq: contact.last_event_seq,
      errorToast: false,
      build: b => b.objectDeleted(contact.entity_id, 'ahoy_contact'),
    });
  },
};

export const companyService = {
  list: (orgId: number, filters?: CRMFilters) => {
    const client = createCRMObjectClient(orgId, 'ahoy_company');
    return client.list(filters);
  },
  get: (orgId: number, entityId: string) => {
    const client = createCRMObjectClient(orgId, 'ahoy_company');
    return client.get(entityId);
  },
  create: async (orgId: number, data: CompanyCreateData, userId: number) => {
    // Extract associations array, overwrite_targets, and draft_entities if present
    const { associations, overwrite_targets, draft_entities, ...properties } =
      data as CompanyCreateData & {
        associations?: Array<{
          association_type_name: string;
          target_entity_id: string;
        }>;
        overwrite_targets?: Array<{
          entity_id: string;
          association_type_name: string;
        }>;
        draft_entities?: DraftEntity[];
      };

    // Coordinates with useCreateCrmObject's instant-creation path — both mint
    // their own client-side entity id independently; whichever submit lands
    // second reconciles the store entry.
    const entityId = crypto.randomUUID();

    // Snapshot once (deep clone) — submitEventsOptimistic's 409-retry rebuild
    // replays the build callback, so it must see the same drafts every time; a
    // deep clone (not a shallow array copy) guarantees a caller mutation of a
    // draft's properties/associations between attempts can't change the replay.
    const drafts: DraftEntity[] = structuredClone(draft_entities ?? []);
    // Parent event count = its own objectCreated + one associationAdded per
    // parent association; passed so the combined parent + draft total is
    // checked against the backend per-batch cap.
    assertValidDraftEntities(drafts, 1 + (associations?.length ?? 0));

    // Not awaited when there are no drafts — see contactService.create.
    // `applyOptimisticBatch` runs before the first `await` inside the helper,
    // so the store already holds the new entity when this returns; the modal
    // closes on that apply while the round-trip, reconciliation and rollback
    // continue detached. The error toast is required because the modal is no
    // longer there to show one.
    //
    // When drafts are present, the caller renders the drafts inline and needs
    // to know the submit outcome, so this IS awaited — with the toast
    // suppressed (the caller shows the error inline instead).
    const submit = submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_company',
      primaryEntityId: entityId,
      overwriteTargets: overwrite_targets,
      ...(drafts.length > 0 ? { errorToast: false as const } : {}),
      build: b => {
        b.objectCreated(entityId, 'ahoy_company', properties);
        if (associations && Array.isArray(associations)) {
          associations.forEach(a =>
            b.associationAdded(
              entityId,
              'ahoy_company',
              a.association_type_name,
              a.target_entity_id
            )
          );
        }
        appendDraftEvents(b, drafts);
      },
    });

    if (drafts.length > 0) {
      try {
        const response = await submit;
        void reconcileOptimisticCreate(
          orgId,
          'ahoy_company',
          entityId,
          response
        );
        void reconcileOptimisticDrafts(orgId, drafts);
        return { entityId };
      } catch (err: unknown) {
        // Helper already rolled back (parent + drafts) and suppressed the
        // toast (errorToast:false → caller renders inline). Log for
        // telemetry (this is the one path with no toast), then re-propagate.
        logger.error(
          'Optimistic CRM create with drafts failed; batch rolled back',
          err instanceof Error ? err : new Error(String(err)),
          {
            component: 'crm_events',
            entityType: 'ahoy_company',
            orgId,
            entityId,
            draftEntityIds: drafts.map(d => d.entity_id),
          }
        );
        throw err;
      }
    }

    void submit
      .then(response => {
        void reconcileOptimisticCreate(
          orgId,
          'ahoy_company',
          entityId,
          response
        );
      })
      .catch(() => {
        // Rollback + toast already handled inside submitEventsOptimistic.
      });

    return { entityId };
  },
  update: (
    orgId: number,
    company: CrmObject,
    data: CompanyUpdateData,
    userId: number
  ) => {
    // Collect all property updates to bundle them into a single event
    const properties: Record<string, unknown> = {};

    if (data.name !== undefined) {
      properties.ahoy_name = data.name;
    }
    if (data.domain !== undefined) {
      properties.ahoy_domain = data.domain;
    }

    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_company',
      primaryEntityId: company.entity_id,
      fallbackSeq: company.last_event_seq,
      errorToast: false,
      build: b =>
        b.propertiesUpdated(company.entity_id, 'ahoy_company', properties),
    });
  },
  delete: (orgId: number, company: CrmObject, userId: number) => {
    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_company',
      primaryEntityId: company.entity_id,
      fallbackSeq: company.last_event_seq,
      errorToast: false,
      build: b => b.objectDeleted(company.entity_id, 'ahoy_company'),
    });
  },
};

export const dealService = {
  list: (orgId: number, filters?: CRMFilters) => {
    const client = createCRMObjectClient(orgId, 'ahoy_deal');
    return client.list(filters);
  },
  get: (orgId: number, entityId: string) => {
    const client = createCRMObjectClient(orgId, 'ahoy_deal');
    return client.get(entityId);
  },
  create: async (orgId: number, data: DealCreateData, userId: number) => {
    // Coordinates with useCreateCrmObject's instant-creation path — both mint
    // their own client-side entity id independently; whichever submit lands
    // second reconciles the store entry.
    const entityId = crypto.randomUUID();

    // Extract associations array, legacy association fields, stage entity ID,
    // and draft_entities. Also strip ahoy_pipeline_stage property - stages are
    // now set via associations
    const {
      associations,
      contact_entity_ids,
      company_entity_id,
      stage_name: _stage_name,
      stage_entity_id,
      ahoy_pipeline_stage: _ahoy_pipeline_stage,
      overwrite_targets,
      draft_entities,
      ...rawProperties
    } = data as DealCreateData & {
      associations?: Array<{
        association_type_name: string;
        target_entity_id: string;
      }>;
      contact_entity_ids?: string[];
      company_entity_id?: string;
      stage_name?: string;
      stage_entity_id?: string;
      ahoy_pipeline_stage?: unknown;
      overwrite_targets?: Array<{
        entity_id: string;
        association_type_name: string;
      }>;
      draft_entities?: DraftEntity[];
    };

    // Build associations array - support both new format and legacy fields
    const finalAssociations = associations || [];
    if (!associations) {
      // Legacy format: convert contact_entity_ids and company_entity_id to associations array
      if (contact_entity_ids && Array.isArray(contact_entity_ids)) {
        for (const contactId of contact_entity_ids) {
          finalAssociations.push({
            association_type_name: 'ahoy_deal_to_contact',
            target_entity_id: contactId,
          });
        }
      }
      if (company_entity_id) {
        finalAssociations.push({
          association_type_name: 'ahoy_deal_to_company',
          target_entity_id: company_entity_id,
        });
      }
    }

    // Add stage association if stage_entity_id is provided
    // This is now the primary way to set a deal's stage (via association, not property)
    if (stage_entity_id) {
      finalAssociations.push({
        association_type_name: 'ahoy_deal_to_deal_pipeline_stage',
        target_entity_id: stage_entity_id,
      });
    }

    // Normalize property values (convert quantity amounts from string to number)
    const properties: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rawProperties)) {
      if (
        value &&
        typeof value === 'object' &&
        'amount' in value &&
        'unit' in value
      ) {
        // This is a quantity value - convert amount to number
        const quantityValue = value as { amount: string; unit: string };
        properties[key] = {
          amount: parseFloat(quantityValue.amount),
          unit: quantityValue.unit,
        };
      } else {
        properties[key] = value;
      }
    }

    // Snapshot once (deep clone) — submitEventsOptimistic's 409-retry rebuild
    // replays the build callback, so it must see the same drafts every time; a
    // deep clone (not a shallow array copy) guarantees a caller mutation of a
    // draft's properties/associations between attempts can't change the replay.
    const drafts: DraftEntity[] = structuredClone(draft_entities ?? []);
    // Parent event count = its own objectCreated + one associationAdded per
    // parent association; passed so the combined parent + draft total is
    // checked against the backend per-batch cap.
    assertValidDraftEntities(drafts, 1 + finalAssociations.length);

    // Not awaited when there are no drafts — see contactService.create.
    // `applyOptimisticBatch` runs before the first `await` inside the helper,
    // so the store already holds the new entity when this returns; the modal
    // closes on that apply while the round-trip, reconciliation and rollback
    // continue detached. The error toast is required because the modal is no
    // longer there to show one.
    //
    // When drafts are present, the caller renders the drafts inline and needs
    // to know the submit outcome, so this IS awaited — with the toast
    // suppressed (the caller shows the error inline instead).
    const submit = submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_deal',
      primaryEntityId: entityId,
      overwriteTargets: overwrite_targets,
      ...(drafts.length > 0 ? { errorToast: false as const } : {}),
      build: b => {
        // Create object WITHOUT associations in payload
        b.objectCreated(entityId, 'ahoy_deal', properties);
        // Add association events separately (all in same batch)
        finalAssociations.forEach(a =>
          b.associationAdded(
            entityId,
            'ahoy_deal',
            a.association_type_name,
            a.target_entity_id
          )
        );
        appendDraftEvents(b, drafts);
      },
    });

    if (drafts.length > 0) {
      try {
        const response = await submit;
        void reconcileOptimisticCreate(orgId, 'ahoy_deal', entityId, response);
        void reconcileOptimisticDrafts(orgId, drafts);
        return { entityId };
      } catch (err: unknown) {
        // Helper already rolled back (parent + drafts) and suppressed the
        // toast (errorToast:false → caller renders inline). Log for
        // telemetry (this is the one path with no toast), then re-propagate.
        logger.error(
          'Optimistic CRM create with drafts failed; batch rolled back',
          err instanceof Error ? err : new Error(String(err)),
          {
            component: 'crm_events',
            entityType: 'ahoy_deal',
            orgId,
            entityId,
            draftEntityIds: drafts.map(d => d.entity_id),
          }
        );
        throw err;
      }
    }

    void submit
      .then(response => {
        void reconcileOptimisticCreate(orgId, 'ahoy_deal', entityId, response);
      })
      .catch(() => {
        // Rollback + toast already handled inside submitEventsOptimistic.
      });

    return { entityId };
  },
  update: (
    orgId: number,
    deal: CrmObject,
    data: DealUpdateData,
    userId: number
  ) => {
    // Collect all property updates to bundle them into a single event
    const properties: Record<string, unknown> = {};

    if (data.name !== undefined) {
      properties.ahoy_name = data.name;
    }
    if (data.amount !== undefined) {
      properties.ahoy_amount = data.amount;
    }
    if (data.close_date !== undefined) {
      properties.ahoy_close_date = data.close_date;
    }

    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_deal',
      primaryEntityId: deal.entity_id,
      fallbackSeq: deal.last_event_seq,
      errorToast: false,
      build: b => b.propertiesUpdated(deal.entity_id, 'ahoy_deal', properties),
    });
  },
  updateStage: (
    orgId: number,
    deal: CrmObject,
    newStageEntityId: string,
    userId: number
  ) => {
    // Find current stage association to remove it
    const currentStageAssociation = deal.associations?.find(
      (a: AssociationDetail) =>
        a.association_type_name === 'ahoy_deal_to_deal_pipeline_stage'
    );

    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_deal',
      primaryEntityId: deal.entity_id,
      fallbackSeq: deal.last_event_seq,
      errorToast: false,
      build: b => {
        // Remove old stage association if exists
        if (currentStageAssociation) {
          b.associationRemoved(
            deal.entity_id,
            'ahoy_deal',
            'ahoy_deal_to_deal_pipeline_stage',
            currentStageAssociation.target_entity_id
          );
        }

        // Add new stage association
        b.associationAdded(
          deal.entity_id,
          'ahoy_deal',
          'ahoy_deal_to_deal_pipeline_stage',
          newStageEntityId
        );
      },
    });
  },
  delete: (orgId: number, deal: CrmObject, userId: number) => {
    return submitEventsOptimistic({
      orgId,
      userId,
      entityTypeName: 'ahoy_deal',
      primaryEntityId: deal.entity_id,
      fallbackSeq: deal.last_event_seq,
      errorToast: false,
      build: b => b.objectDeleted(deal.entity_id, 'ahoy_deal'),
    });
  },
};

// ===========================
// Label Services (Old CRM App - Unchanged)
// ===========================

export const labelService = {
  list: async (
    orgId: number,
    filters?: LabelFilters
  ): Promise<LabelsListResponse> => {
    const queryString = filters ? buildQueryString(filters) : '';
    const url = `/api/orgs/${orgId}/crm/labels/${queryString ? `?${queryString}` : ''}`;
    return apiCall<LabelsListResponse>(url);
  },

  get: async (orgId: number, labelId: number): Promise<Label> => {
    return apiCall<Label>(`/api/orgs/${orgId}/crm/labels/${labelId}/`);
  },

  create: async (orgId: number, data: LabelCreateUpdate): Promise<Label> => {
    return apiCall<Label>(`/api/orgs/${orgId}/crm/labels/`, 'POST', data);
  },

  update: async (
    orgId: number,
    labelId: number,
    data: Partial<LabelCreateUpdate>
  ): Promise<Label> => {
    return apiCall<Label>(
      `/api/orgs/${orgId}/crm/labels/${labelId}/`,
      'PATCH',
      data
    );
  },

  delete: async (orgId: number, labelId: number): Promise<void> => {
    return apiCall<undefined>(
      `/api/orgs/${orgId}/crm/labels/${labelId}/`,
      'DELETE'
    );
  },
};

// Export combined service for backward compatibility
export const crmService = {
  contacts: contactService,
  companies: companyService,
  deals: dealService,
  labels: labelService,
};

export default crmService;
